import { useState } from "react";
import { Card, CardContent, Typography, Box, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useScopeOfWork } from "../../context/ScopeOfWorkContext";
import { Place } from "../../context/scope-of-work.types";
import { colors, alphaVar } from "../../../../../../ui/colors";
import { menuPropsModal } from "../../../../../../../utils";

const SQUARE_FT_RANGES = [
  { value: "0-1000", label: "0 - 1,000 sq ft" },
  { value: "1001-2000", label: "1,001 - 2,000 sq ft" },
  { value: "2001-3000", label: "2,001 - 3,000 sq ft" },
  { value: "3001-4000", label: "3,001 - 4,000 sq ft" },
  { value: "4001-5000", label: "4,001 - 5,000 sq ft" },
  { value: "5001", label: "5,001+ sq ft" },
];

const COLORS = {
  orange: {
    base: colors.orange.base,
    50: "#FFF3E8",
    100: "#FFE4CC",
    700: "#C96A1B",
  },
  gray: {
    100: "#F3F4F6",
    300: "#D1D5DB",
    500: "#6B7280",
    700: "#374151",
  },
};

interface TypeOfPlaceCardProps {
  place: Place;
  onSelect: (place: Place) => void;
  selected: boolean;
}

export const TypeOfPlaceCard = ({ place, onSelect, selected }: TypeOfPlaceCardProps) => {
  const { state, dispatch } = useScopeOfWork();

  const handleRangeChange = (e: any) => {
    const value = e.target.value;
    dispatch({ type: "SET_SQUAREFT", payload: value });
  };

  return (
    <Card
      onClick={() => onSelect(place)}
      sx={{
        backgroundColor: selected ? alphaVar(COLORS.orange[50], 0.6) : "var(--fx-surface-base, #FFFFFF)",
        borderRadius: 2,
        border: `2px solid ${selected ? COLORS.orange.base : alphaVar(COLORS.gray[100], 0.8)}`,
        boxShadow: selected ? `0 3px 8px ${alphaVar(COLORS.orange.base, 0.25)}` : "0 1px 2px rgba(0,0,0,0.05)",
        cursor: "pointer",
        transition: "all 0.2s ease",
        overflow: "hidden",
        maxWidth: 250,
        "&:hover": {
          transform: "translateY(-1px)",
          boxShadow: `0 3px 6px ${alphaVar(COLORS.orange.base, 0.15)}`,
          borderColor: alphaVar(COLORS.orange.base, 0.3),
        },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          flex: "1 1 auto",
          p: 1.5,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
        }}
      >
        <Box
          component="img"
          src={place.imageUrl}
          alt={place.name}
          sx={{
            width: 170,
            height: 170,
            borderRadius: "50%",
            color: COLORS.orange.base,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "auto",
            marginTop: -3,
            mb: 1,
            flexShrink: 0,
            objectFit: "contain",
          }}
        />

        <Typography
          variant="subtitle2"
          fontWeight={700}
          sx={{
            color: selected ? COLORS.orange[700] : COLORS.gray[500],
            fontSize: 16,
            mb: 0.5,
            marginTop: -4,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
        >
          {place.name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: COLORS.gray[300],
            fontSize: 14,
            lineHeight: 1.2,
            mb: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {place.description}
        </Typography>

        <Box
          sx={{
            px: 0.7,
            py: 0.2,
            fontSize: 9,
            fontWeight: 600,
            textTransform: "uppercase",
            borderRadius: 1,
            backgroundColor: alphaVar(COLORS.orange[100], 0.25),
            color: COLORS.orange[700],
            width: "fit-content",
            mb: selected ? 1.5 : 0,
          }}
        >
          {place.category?.name || "—"}
        </Box>

        {selected && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Square footage range</InputLabel>
              <Select MenuProps={menuPropsModal} label="Square footage range" value={state.squareft || ""} onChange={handleRangeChange}>
                {SQUARE_FT_RANGES.map((range) => (
                  <MenuItem key={range.value} value={range.value}>
                    {range.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
